window.QtyLimit = (function () {
  const MAX_PER_PRODUCT = 12;
  const TOOLTIP_DURATION_MS = 3000;
  let activeTooltipTimeout = null;

  async function getCartQtyByProduct() {
    const res = await fetch(`${window.Shopify?.routes?.root || "/"}cart.js`, {
      headers: { Accept: "application/json" },
    });
    const cart = await res.json();
    const byProduct = {};
    cart.items.forEach((item) => {
      byProduct[item.product_id] =
        (byProduct[item.product_id] || 0) + item.quantity;
    });
    return byProduct;
  }

  function getMaxForProduct(maxForProduct) {
    const parsed = Number.parseInt(maxForProduct, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : MAX_PER_PRODUCT;
  }

  async function remainingAllowance(
    productId,
    maxForProduct = MAX_PER_PRODUCT,
  ) {
    const max = getMaxForProduct(maxForProduct);
    const map = await getCartQtyByProduct();
    const inCart = map[String(productId)] || map[Number(productId)] || 0;
    return Math.max(0, max - inCart);
  }

  function isQuantityContext(target) {
    if (!target) return false;
    return (
      target.matches(".quantity__input, .quick-add-card__qty-input") ||
      Boolean(target.closest("quantity-input, .quick-add-card__qty"))
    );
  }

  function getTooltipAnchor(target) {
    if (!target) return null;
    // Wider ancestors first — a single closest() with grouped selectors
    // would stop at the narrow quantity-input inside quick-add-card.
    return (
      target.closest(".product-form__quantity") ||
      target.closest(".quick-add-card__actions") ||
      target.closest(".quick-add-card__panel") ||
      target.closest("quick-add-bulk") ||
      target.closest("quantity-input") ||
      target.closest(".quick-add-card__qty") ||
      target
    );
  }

  function removeLegacyNotes(anchor) {
    anchor
      ?.querySelectorAll(".qty-limit-note")
      .forEach((note) => note.remove());
  }

  function showTooltip(target, text) {
    const anchor = getTooltipAnchor(target);
    if (!anchor || !text) return;

    const scope =
      anchor.closest(
        ".quick-add-card__panel, .product-form__quantity, quick-add-bulk",
      ) || anchor.parentElement;
    scope?.querySelectorAll(".qty-limit-tooltip").forEach((el) => {
      if (el.parentElement !== anchor) el.remove();
    });

    removeLegacyNotes(anchor);

    let tooltip = anchor.querySelector(".qty-limit-tooltip");
    if (!tooltip) {
      tooltip = document.createElement("div");
      tooltip.className = "qty-limit-tooltip";
      tooltip.setAttribute("role", "status");
      tooltip.setAttribute("aria-live", "polite");
      anchor.appendChild(tooltip);
    }

    tooltip.textContent = text;
    tooltip.classList.add("is-visible");

    if (activeTooltipTimeout) {
      clearTimeout(activeTooltipTimeout);
    }

    activeTooltipTimeout = setTimeout(() => {
      tooltip.classList.remove("is-visible");
      activeTooltipTimeout = null;
    }, TOOLTIP_DURATION_MS);
  }

  function showMessage(target, text) {
    if (!target) return;

    if (isQuantityContext(target)) {
      showTooltip(target, text);
      return;
    }

    const cartItem = target.closest('.cart-item, [id^="CartDrawer-Item-"]');
    if (cartItem) {
      const lineIndex = target.dataset.index;
      const lineItemError =
        document.getElementById(`Line-item-error-${lineIndex}`) ||
        document.getElementById(`CartDrawer-LineItemError-${lineIndex}`);
      const errorText = lineItemError?.querySelector(".cart-item__error-text");
      if (errorText) {
        errorText.textContent = text;
        return;
      }
    }

    showTooltip(target, text);
  }

  function getLineAllowance(container, productId, currentInput) {
    const maxForProduct = getMaxForProduct(
      currentInput?.dataset?.maxPerProduct,
    );
    const inputs = container.querySelectorAll(
      `.quantity__input[data-product-id="${productId}"]`,
    );
    let otherLinesQty = 0;
    inputs.forEach((input) => {
      if (input !== currentInput) {
        otherLinesQty += parseInt(input.value, 10) || 0;
      }
    });
    return Math.max(0, maxForProduct - otherLinesQty);
  }

  function updateCartLineMaxAttributes(container) {
    if (!container) return;
    container
      .querySelectorAll(".quantity__input[data-product-id]")
      .forEach((input) => {
        const productId = input.dataset.productId;
        const allowance = getLineAllowance(container, productId, input);
        input.setAttribute("max", allowance);
      });
  }

  function initQuantityInputClamp() {
    document.addEventListener("change", async (event) => {
      const input = event.target;
      if (
        !input.matches(
          ".quantity__input[data-max-per-product], .quick-add-card__qty-input[data-max-per-product]",
        )
      ) {
        return;
      }

      const productId = input.dataset.productId;
      if (!productId) return;

      try {
        const maxForProduct = getMaxForProduct(input.dataset.maxPerProduct);
        const remaining = await remainingAllowance(productId, maxForProduct);
        const minValue = Number.parseInt(input.min, 10) || 1;
        const value = Number.parseInt(input.value, 10) || minValue;

        if (value > remaining) {
          input.value = String(Math.max(remaining, minValue));
          if (remaining === 0) {
            showTooltip(
              input,
              `You already have the maximum of ${maxForProduct} bottles of this wine in your cart.`,
            );
          } else {
            showTooltip(
              input,
              `You can add ${remaining} more (max ${maxForProduct} of this item).`,
            );
          }
        }
      } catch (e) {
        console.error(e);
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initQuantityInputClamp);
  } else {
    initQuantityInputClamp();
  }

  return {
    MAX_PER_PRODUCT,
    getMaxForProduct,
    getCartQtyByProduct,
    remainingAllowance,
    showMessage,
    showTooltip,
    getLineAllowance,
    updateCartLineMaxAttributes,
  };
})();
